import { AlertCircle, SearchIcon, Square, SquareCheck } from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface Option {
  value: string;
  label: string;
}

interface SelectWithCheckboxProps {
  label: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  required?: boolean;
  id?: string;
  placeholder?: string;
  maxHeight?: string;
  disabled?: boolean;
  showAbove?: boolean;
  /** When true with showAbove={false}, limits dropdown height to available space below the field */
  clampToViewportBelow?: boolean;
  className?: string;
  labelClassName?: string;
  selectedText?: string;
}

const SelectWithCheckbox: React.FC<SelectWithCheckboxProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  id,
  placeholder = "Select options",
  maxHeight = "450px",
  disabled = false,
  showAbove = true,
  clampToViewportBelow = false,
  className = "",
  selectedText = "selected",
  labelClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rafRef = useRef<number | null>(null);
  const showSearch = options.length >= 5;

  // Get selected options labels
  const getSelectedLabels = () => {
    if (!value || value.length === 0) return placeholder;

    const selectedOptions = options.filter((option) =>
      value.includes(option.value)
    );

    const selectedOptionsText = (selectedOptions?.length > 1) ? `${selectedText}` : selectedOptions[0]?.label;

    if (selectedOptions.length > 0) {
      return (
        <div className="flex items-center w-full">
          {
            selectedOptions.length > 1 &&
            <div className="text-[#2165C5] text-[14px] font-[500] min-w-[32px] h-[32px] bg-[#DEEAF7] rounded-full flex items-center justify-center flex-shrink-0">
              {selectedOptions.length}
            </div>
          }
          <span
            className={`text-[#A0AEC0] text-[14px] font-[500] ml-2 truncate ${disabled ? "text-[#A0AEC0]" : "text-[#1E1E1E]"
              }`}
            style={{
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
              maxWidth: "calc(100% - 40px)",
            }}
          >
            {selectedOptionsText || "selected"}
          </span>
        </div>
      );
    }

    return placeholder;
  };

  const getDropdownMaxHeight = (availableSpace?: number) => {
    const parsedMaxHeight = Number.parseInt(maxHeight, 10) || 450;
    const responsiveCap = parsedMaxHeight;

    if (availableSpace != null && clampToViewportBelow) {
      return Math.max(Math.min(responsiveCap, availableSpace), 120);
    }

    return responsiveCap;
  };

  // Function to update dropdown position
  const updateDropdownPosition = () => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownWidth = selectRect.width;
      const viewportPadding = 8;
      const minUsableHeight = 180;
      const spaceBelow = Math.max(
        0,
        window.innerHeight - selectRect.bottom - viewportPadding
      );
      const spaceAbove = Math.max(0, selectRect.top - viewportPadding);
      const placeAbove = showAbove
        ? spaceAbove > spaceBelow && spaceAbove >= minUsableHeight
        : spaceBelow < minUsableHeight && spaceAbove > spaceBelow;
      const availableSpace = placeAbove ? spaceAbove : spaceBelow;

      const style: React.CSSProperties = {
        position: "fixed",
        left: Math.round(selectRect.left),
        width: Math.round(dropdownWidth),
        minWidth: Math.round(dropdownWidth),
        zIndex: 9999,
        overflowY: "auto",
        maxHeight: getDropdownMaxHeight(
          clampToViewportBelow || placeAbove ? availableSpace : undefined
        ),
      };

      if (placeAbove) {
        const computedHeight =
          typeof style.maxHeight === "number"
            ? style.maxHeight
            : Number.parseInt(String(style.maxHeight), 10) || 450;
        style.top = Math.max(
          viewportPadding,
          Math.round(selectRect.top) - computedHeight
        );
      } else {
        style.top = Math.round(selectRect.bottom);
      }

      setDropdownStyle(style);
    }
  };

  // Position dropdown when it opens and on window resize/scroll
  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      const scheduleUpdate = () => {
        if (rafRef.current !== null) return;
        rafRef.current = window.requestAnimationFrame(() => {
          updateDropdownPosition();
          rafRef.current = null;
        });
      };

      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);

      return () => {
        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [isOpen, showAbove, clampToViewportBelow, maxHeight]);

  // Filter options based on search term while preserving original order
  useEffect(() => {
    // Always maintain the original order from the options array
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      // Filter while preserving the original order
      // Convert search term to lowercase for case-insensitive comparison
      const searchLower = searchTerm.toLowerCase();

      const filtered = options.filter((option) => {
        // Skip the placeholder option (empty value with placeholder text)
        if (option.value === "" && option.label === placeholder) {
          return false;
        }
        // Check if the option label includes the search term (case insensitive)
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, placeholder]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, showSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when parent/page scrolls (outside-click-like behavior)
  useEffect(() => {
    if (!isOpen) return;
    const openedAt = Date.now();

    const handleScrollClose = (event: Event) => {
      if (Date.now() - openedAt < 180) {
        return;
      }
      const targetNode = event.target as Node | null;
      if (
        targetNode &&
        ((dropdownRef.current && dropdownRef.current.contains(targetNode)) ||
          (selectRef.current && selectRef.current.contains(targetNode)))
      ) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScrollClose, true);
    return () => {
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [isOpen]);

  // Handle checkbox change
  const handleCheckboxChange = (optionValue: string) => {
    if (!disabled) {
      const newValue = [...value];
      const index = newValue.indexOf(optionValue);

      if (index === -1) {
        // Add the value if it's not already selected
        newValue.push(optionValue);
      } else {
        // Remove the value if it's already selected
        newValue.splice(index, 1);
      }

      onChange(newValue);
    }
  };

  // Handle "Select all" checkbox
  const handleSelectAll = (checked: boolean) => {
    if (!disabled) {
      if (checked) {
        // Select all filtered options
        const allValues = filteredOptions.map((option) => option.value);
        onChange(allValues);
      } else {
        // Deselect all
        onChange([]);
      }
    }
  };

  // Check if all filtered options are selected
  const areAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => value.includes(option.value));

  return (
    <div className="w-full flex flex-col space-y-2">
      {label && (
        <label htmlFor={id} className="text-[#2D3748] text-[16px] font-medium">
          {label}
          {required && <span className="text-[#ef4444] ml-1">*</span>}
        </label>
      )}
      <div className="relative" id={id} ref={selectRef}>
        <div
          className={`w-full min-h-[50px] px-[20px] py-[8px] border ${error ? "border-[#FF3B30]" : "border-[#E2E8F0]"
            } rounded-[15px] flex items-center justify-between ${disabled ? "" : "cursor-pointer"
            } ${className}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span
            className={`text-[#1F1F1F] text-[14px] font-[400] flex-grow overflow-hidden ${value && value.length > 0
                ? `${disabled ? "text-[#A0AEC0]" : ""}`
                : "text-[#A0AEC0]"
              } ${labelClassName}`}
          >
            {getSelectedLabels()}
          </span>
          {!disabled && (
            <svg
              className={`w-6 h-6 text-[#A0AEC0] transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          )}
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={`bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom ${showAbove ? "" : ""
                }`}
              style={dropdownStyle}
            >
              {showSearch && (
                <div className="p-2 sticky top-0 bg-white z-20">
                  <div className="relative z-20 rounded-[12px] bg-white border border-gray-200">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <SearchIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full pl-10 pr-3 py-2 rounded-md text-sm focus:outline-none"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Options List */}
              <div className="p-2">
                {/* Select All Option */}
                {options.length > 0 && (
                  <div
                    className="flex items-center p-1 hover:bg-gray-50 rounded cursor-pointer w-full"
                    onClick={() => handleSelectAll(!areAllSelected)}
                  >
                    <div className="flex-shrink-0 min-w-[24px]">
                      {areAllSelected ? (
                        <SquareCheck className="w-6 h-6 text-[#5C88C5]" />
                      ) : (
                        <Square className="w-6 h-6 text-[#354052]" />
                      )}
                    </div>
                    <label
                      htmlFor="select-all"
                      className="ml-2 text-[#354052] cursor-pointer"
                    >
                      Select all
                    </label>
                  </div>
                )}
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center p-1 hover:bg-gray-50 rounded cursor-pointer w-full"
                    onClick={() => handleCheckboxChange(option.value)}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-shrink-0 min-w-[24px]">
                        {value.includes(option.value) ? (
                          <SquareCheck className="w-6 h-6 text-[#5C88C5]" />
                        ) : (
                          <Square className="w-6 h-6 text-[#354052]" />
                        )}
                      </div>

                      {/* <Checkbox
                        id={`option-${option.value}`}
                        checked={value.includes(option.value)}
                        onCheckedChange={() =>
                          handleCheckboxChange(option.value)
                        }
                      /> */}
                      <label
                        htmlFor={`option-${option.value}`}
                        className="ml-2 text-[#354052] cursor-pointer break-words overflow-wrap-anywhere flex-grow"
                      >
                        {option.label}
                      </label>
                    </div>
                  </div>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No options found
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="text-xs text-[#ef4444] flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectWithCheckbox;
